import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';

import {
  AntibodyComponentSchemaRole,
  AntibodyRelatedSchemaRole,
  AntibodyRootSchemaRole,
} from 'graphql-client/__generated/graphqlGlobalTypes';

import {AntibodyEnforcedDataModelSpecsQuery} from './__generated/AntibodyEnforcedDataModelSpecsQuery';

export const ENFORCED_FIELD_SPECS_FRAGMENT: DocumentNode = gql`
  fragment EnforcedFieldSpecsFragment on AntibodyEnforcedSchemaFieldSpecNode {
    fieldName
    systemName
    fieldType
    fieldRole
    isRequired
    isMulti
    dropdown {
      name
      allowedOptions
      compatibleDropdowns {
        id
        name
        schemaFieldSelectorOptions {
          id
          name
          archiveRecord {
            purpose
          }
        }
        allSchemaFieldSelectorOptions {
          id
          name
          archiveRecord {
            purpose
          }
        }
        archiveRecord {
          purpose
        }
        registry {
          id
        }
      }
    }
    linkedInterface {
      id
      name
    }
    unit {
      id
      symbol
    }
    computedFieldSpec {
      formulaName
    }
    autofillParamsSpec {
      overlappingEnabled
      minComponentLength
      maxComponentLength
      numLongestComponents
    }
  }
`;

export const ENFORCED_CONSTRAINT_SPEC_FRAGMENT: DocumentNode = gql`
  fragment EnforcedConstraintSpecFragment on AntibodyEnforcedConstraintSpecNode {
    enforcedTypeSpecificConstraint {
      constraintType
      isCaseSensitive
    }
    enforcedMinimumFieldDefinitionNames
  }
`;

export const ANTIBODY_ENFORCED_DATA_MODEL_SPECS_QUERY: DocumentNode = gql`
  query AntibodyEnforcedDataModelSpecsQuery @skipAudit @engComponent(name: ANTIBODIES) {
    antibodyEnforcedSchemaSpecs {
      ... on AntibodyEnforcedRelatedSchemaSpecNode {
        relatedSchemaRole: schemaRole
        enforcedFieldSpecs {
          ...EnforcedFieldSpecsFragment
        }
        enforcedConstraintSpec {
          ...EnforcedConstraintSpecFragment
        }
        enforcedNamingStrategySpec {
          validNamingStrategies
        }
      }
      ... on AntibodyEnforcedComponentSchemaSpecNode {
        componentSchemaRole: schemaRole
        enforcedFieldSpecs {
          ...EnforcedFieldSpecsFragment
        }
        enforcedConstraintSpec {
          ...EnforcedConstraintSpecFragment
        }
        enforcedNamingStrategySpec {
          validNamingStrategies
        }
      }
      ... on AntibodyEnforcedRootSchemaSpecNode {
        rootSchemaRole: schemaRole
        enforcedFieldSpecs {
          ...EnforcedFieldSpecsFragment
        }
        enforcedConstraintSpec {
          ...EnforcedConstraintSpecFragment
        }
        enforcedNamingStrategySpec {
          validNamingStrategies
        }
      }
    }
  }
  ${ENFORCED_FIELD_SPECS_FRAGMENT}
  ${ENFORCED_CONSTRAINT_SPEC_FRAGMENT}
`;

export type AntibodyEnforcedSchemaSpec =
  AntibodyEnforcedDataModelSpecsQuery['antibodyEnforcedSchemaSpecs'][number];

/** Any schema role that may appear on {@link AntibodyEnforcedSchemaSpec}. */
export type AntibodyEnforcedSchemaRole =
  | AntibodyRootSchemaRole
  | AntibodyComponentSchemaRole
  | AntibodyRelatedSchemaRole;

export function getAntibodyEnforcedSchemaRoleFromSpec(
  spec: AntibodyEnforcedSchemaSpec
): AntibodyEnforcedSchemaRole {
  if (spec.__typename === 'AntibodyEnforcedRelatedSchemaSpecNode') {
    return spec.relatedSchemaRole;
  }
  if (spec.__typename === 'AntibodyEnforcedComponentSchemaSpecNode') {
    return spec.componentSchemaRole;
  }
  return spec.rootSchemaRole;
}

/** Whether the given role is a sub-schema role (i.e. not the top-level antibody schema). */
export function isAntibodySubSchemaRole(
  role: AntibodyEnforcedSchemaRole
): role is AntibodyComponentSchemaRole | AntibodyRelatedSchemaRole {
  return role !== AntibodyRootSchemaRole.ANTIBODY;
}

export type AntibodyEnforcedSchemaFieldSpec = AntibodyEnforcedSchemaSpec['enforcedFieldSpecs'][number];

export type AntibodyEnforcedConstraintSpec = Exclude<
  AntibodyEnforcedSchemaSpec['enforcedConstraintSpec'],
  null
>;

export type AntibodyEnforcedNamingStrategySpec = Exclude<
  AntibodyEnforcedSchemaSpec['enforcedNamingStrategySpec'],
  null
>;
